import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Menu, MenuItem, Skeleton, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useProfileData } from "../../../hooks/useProfileData";
import { logout } from "../../../firebase/firebase";
import { colors } from "../../../theme/palette";

import { ArrowDownIcon, ChatIcon, GlobeIcon, ProfileIcon } from "../../../assets/icons/MUI/LayoutIcons";
import LogoIcon from "../../../assets/images/Layout/goalngo-nav-logo.png";
import typography from "../../../theme/typography";
import { deletePushNotifications } from "../../../api/notifications";
import useLanguageToggle from "../../../locale/useChangeLang";

import SearchBar from "./SearchBar";
import Notifications from "../Notifications";
import APP_ROUTES from "../../../routers/routeConstants";

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { toggleLanguage } = useLanguageToggle();

  const { data: userData, isLoading } = useProfileData();
  const { fullName } = userData;

  const myQueryClient = useQueryClient();

  return (
    <Box
      component="nav"
      sx={{
        width: "100%",
        background: colors.white,
        padding: "11px 0",
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1140px",
          margin: "0 auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "100px" }}>
          <Link to={APP_ROUTES.DASHBOARD} style={{ height: "34px" }}>
            <img src={LogoIcon} width="94.568px" height="34px" />
          </Link>
          <SearchBar />
        </Box>
        <Box sx={{ display: "flex", gap: "9px", alignItems: "center" }}>
          <IconButton onClick={toggleLanguage} sx={{ position: "relative" }}>
            <Typography
              variant="body10Bold"
              sx={{
                position: "absolute",
                top: "2px",
                right: "0",
                backgroundColor: colors.Green100,
                padding: "2px",
                borderRadius: "50%",
                color: colors.White,
              }}
            >
              {currentLanguage === "ar" ? "EN" : "AR"}
            </Typography>
            <GlobeIcon />
          </IconButton>
          <Link to={APP_ROUTES.CHAT}>
            <IconButton>
              <ChatIcon />
            </IconButton>
          </Link>
          <Notifications />
          <div>
            <Button
              sx={{
                ...typography.body12Regular,
                color: colors.black100,
                display: "flex",
                gap: "6px",
                borderRadius: "5px",
              }}
              onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
            >
              <ProfileIcon />
              {isLoading ? <Skeleton variant="text" width="40px" animation="wave" /> : fullName}
              <ArrowDownIcon />
            </Button>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={() => {
                setAnchorEl(null);
              }}
            >
              <MenuItem
                sx={{
                  ...typography.body12Regular,
                  color: colors.black100,
                }}
                onClick={async () => {
                  setAnchorEl(null);
                  localStorage.removeItem("entityId");
                  await deletePushNotifications();
                  await logout();
                  myQueryClient.clear();
                  navigate(APP_ROUTES.HOME);
                  window.location.reload();
                }}
              >
                {t("layout.nav.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default NavBar;
